import { usePage } from "@inertiajs/react"
import type { JSONContent } from "@tiptap/core"
import { History } from "lucide-react"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import type { CameraPickerPreset } from "@/components/cameras/camera-picker-dialog"
import { FlowPromptComposer, type FlowPromptChrome } from "@/components/flow/flow-prompt-composer"
import { PromptFieldLoadingFrame } from "@/components/prompt-editor/prompt-field-loading-frame"
import { FlowShotReferences } from "@/components/flow/flow-shot-references"
import { GenerateWithModelLabel } from "@/components/media/generate-with-model-label"
import { GenerationBar } from "@/components/media/generation-bar"
import { GenerationAttributeBar } from "@/components/media/generation-attribute-bar"
import {
  buildMentionItems,
  type CastRow,
  type LocationRow,
  type PropRow,
} from "@/components/prompt-editor/mention-sources"
import { PromptEditor, type PromptEditorHandle } from "@/components/prompt-editor/prompt-editor"
import {
  FirstGenResolutionDialog,
  useFirstGenResolutionGate,
} from "@/components/shot-panel/first-gen-resolution-dialog"
import { enqueueSave } from "@/components/shot-panel/enqueue-save"
import { PromptVersionsDialog } from "@/components/shot-panel/prompt-versions-dialog"
import { ShotCameraSelect } from "@/components/shot-panel/shot-camera-select"
import { useShotVideoGenerationSettings } from "@/components/shot-panel/use-shot-video-generation-settings"
import { shotRefCharacter, shotRefLocation, shotRefProps } from "@/components/shot-reference-entities"
import { ShotReferencesPanel, type ReferenceImage } from "@/components/shot-references-panel"
import { aspectRatiosToFrameFormats } from "@/components/ui/frame-format-select"
import { Button } from "@/components/ui/primitives"
import { useSeedanceVideoConsent } from "@/hooks/use-seedance-video-consent"
import { useTranslation } from "@/hooks/use-translation"
import { fetchAvailableCameraPresets, getCachedAvailableCameraPresets } from "@/lib/camera-presets-api"
import { aspectRatioForModel } from "@/lib/canvas-model-defaults"
import { confirmDelayKey } from "@/lib/confirm-delay-registry"
import { syncFlowShotLinks } from "@/lib/flow-api"
import type { FlowAttachOutcome, FlowAttachRequest } from "@/lib/flow-attach-input"
import {
  effectiveFrameFormat,
  FRAME_FORMATS,
  isExplicitShotFrameOverride,
  normalizeFrameFormat,
  type FrameFormat,
} from "@/lib/frame-format"
import {
  patchShotFrameFormat,
  patchShotVideoDraft,
  patchShotVideoModelKey,
  patchShotVideoResolution,
  patchShotVideoSettings,
  runWorkspace,
} from "@/lib/inertia-actions"
import { seedanceDraftEligible } from "@/lib/seedance-draft"
import { activeShotJob, TAB_JOB_KINDS, useReloadOnJobFinish } from "@/lib/shot-jobs"
import type { ShotReferenceInputRow } from "@/lib/shot-reference-inputs-api"
import {
  effectiveShotVideoModelKey,
  effectiveShotVideoResolution,
  findShotVideoModel,
  inheritedShotVideoResolution,
  videoResolutionAfterModelChange,
} from "@/lib/shot-video-settings"
import { cn } from "@/lib/utils"
import {
  ADAPTIVE_DURATION_SEC,
  hasVideoReferenceForAdaptive,
  nearestDurationSec,
  nextShotDurationPatch,
} from "@/lib/video-duration"
import {
  fetchShotWorkspaceDetail,
  patchShotWorkspacePrompts,
  type PatchShotWorkspacePromptsBody,
} from "@/lib/workspace-api"
import { useWorkspaceJobsStore } from "@/stores/workspace-jobs-store"
import type { CanvasModel } from "@/types/canvas"
import type { FlowNodeRow } from "@/types/flow"
import type { GenerationModelCatalogEntry, WorkspaceBaseProps } from "@/types/workspace"

export type FlowShotAgentPending = {
  text?: string
  cameraPresetIds?: number[]
  durationTargetSec?: number
}

export type FlowShotPromptHandle = {
  applyExternalPrompt: (payload: { text: string; cameraPresetIds?: number[] }) => void
  applyDuration: (durationTargetSec: number) => void
}

type FlowShotPromptProps = {
  projectId: number
  frameFormat: string
  frameRate?: number
  shotId: number
  node: FlowNodeRow
  canEdit: boolean
  onDisconnect: (edgeId: number) => void
  onMakeKeyframe: (sourceNodeId: number) => Promise<void>
  onAttach: (batch: FlowAttachRequest) => Promise<FlowAttachOutcome>
  onNodesChange: (nodes: FlowNodeRow[]) => void
  consumePendingAgent: (shotId: number) => FlowShotAgentPending | null
  chrome?: FlowPromptChrome
}

type ShotMedia = {
  id: number
  shotKind: "regular" | "transition"
  videoDefault: string
  videoSystemDefault: string
  durationTargetSec: number | null
  frameFormat: string | null
  videoModelKey: string | null
  videoResolution: string | null
  videoDraft: boolean | null
  videoShotType: string | null
  seedanceAdaptiveDuration: boolean
  hasVideoReferenceInput: boolean
  storyboardPreviewUrl: string | null
  videoClipUrl: string | null
  providerTransient: boolean
  characterIds: number[]
  locationId: number | null
  propAssignments: { prop_id: number; state_id: number | null }[]
  videoRefs: ShotReferenceInputRow[]
  audioRefs: ShotReferenceInputRow[]
  characters: { name: string; outfitName: string | null }[]
  locationName: string | null
  locationVariantName: string | null
  referenceImages: ReferenceImage[]
  cameraPresetIds: number[]
  videoText: string
  videoDoc: JSONContent | null
  videoSystem: string
}

type FlowShotPageProps = WorkspaceBaseProps & {
  project_cast?: CastRow[]
  project_locations?: LocationRow[]
  project_props?: PropRow[]
}

function norm(value: string) {
  return value.replace(/\r\n/g, "\n").trim()
}

// Survives the prompt tab unmount so the next open waits for the flush save.
let shotPromptSaveTail: Promise<unknown> = Promise.resolve()

function sameIds(left: number[], right: number[]): boolean {
  if (left.length !== right.length) return false
  return left.every((id, index) => id === right[index])
}

function numberIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((id): id is number => typeof id === "number" && id > 0)
}

function referenceRows(raw: unknown): ShotReferenceInputRow[] {
  return Array.isArray(raw) ? (raw as ShotReferenceInputRow[]) : []
}

function propAssignments(raw: unknown): ShotMedia["propAssignments"] {
  if (!Array.isArray(raw)) return []
  const rows: ShotMedia["propAssignments"] = []
  for (const row of raw) {
    if (!row || typeof row !== "object") continue
    const id = (row as { id?: unknown }).id
    if (typeof id !== "number") continue
    const stateId = (row as { state_id?: unknown }).state_id
    rows.push({ prop_id: id, state_id: typeof stateId === "number" ? stateId : null })
  }
  return rows
}

function shotCharacters(raw: unknown): ShotMedia["characters"] {
  if (!Array.isArray(raw)) return []
  const rows: ShotMedia["characters"] = []
  for (const row of raw) {
    if (!row || typeof row !== "object") continue
    const name = (row as { name?: unknown }).name
    if (typeof name !== "string" || !name) continue
    const outfit = (row as { outfit_name?: unknown }).outfit_name
    rows.push({ name, outfitName: typeof outfit === "string" && outfit ? outfit : null })
  }
  return rows
}

function optionalString(raw: unknown): string | null {
  return typeof raw === "string" && raw ? raw : null
}

function knownPresetIds(ids: number[], presets: CameraPickerPreset[]): number[] {
  const positive = ids.filter((id) => Number.isFinite(id) && id > 0)
  if (presets.length === 0) return positive
  const known = new Set(presets.map((preset) => preset.id))
  return positive.filter((id) => known.has(id))
}

function mapShotDetail(raw: Record<string, unknown>): ShotMedia {
  const extras = raw.llm_extras && typeof raw.llm_extras === "object" ? (raw.llm_extras as Record<string, unknown>) : {}
  const inputs = raw.shot_reference_inputs
  const inputRecord = inputs && typeof inputs === "object" ? (inputs as Record<string, unknown>) : {}
  const videoRefs = referenceRows(inputRecord.video)
  const audioRefs = referenceRows(inputRecord.audio)
  const videoDefault = norm(typeof raw.video_kling_prompt_default === "string" ? raw.video_kling_prompt_default : "")
  const custom = typeof raw.video_kling_prompt === "string" ? raw.video_kling_prompt.trim() : ""
  const videoText = norm(custom || videoDefault)
  const videoSystemDefault = norm(
    typeof raw.video_system_prompt_default === "string" ? raw.video_system_prompt_default : "",
  )
  const videoSystem = norm(
    typeof raw.video_system_prompt === "string" && raw.video_system_prompt.trim()
      ? raw.video_system_prompt
      : videoSystemDefault,
  )
  return {
    id: raw.id as number,
    shotKind: raw.shot_kind === "transition" ? "transition" : "regular",
    videoDefault,
    videoSystemDefault,
    durationTargetSec: typeof raw.duration_target_sec === "number" ? raw.duration_target_sec : null,
    frameFormat: typeof raw.frame_format === "string" && raw.frame_format ? raw.frame_format : null,
    videoModelKey: typeof raw.video_model_key === "string" ? raw.video_model_key : null,
    videoResolution: typeof raw.video_resolution === "string" ? raw.video_resolution : null,
    videoDraft: raw.video_draft === true ? true : raw.video_draft === false ? false : null,
    videoShotType: typeof extras.video_shot_type === "string" ? extras.video_shot_type : null,
    seedanceAdaptiveDuration: Boolean(extras.seedance_adaptive_duration),
    hasVideoReferenceInput: hasVideoReferenceForAdaptive(videoRefs.length, extras),
    storyboardPreviewUrl: typeof raw.storyboard_preview_url === "string" ? raw.storyboard_preview_url : null,
    videoClipUrl: typeof raw.video_clip_url === "string" ? raw.video_clip_url : null,
    providerTransient: extras.video_generation_provider_transient === true,
    characterIds: numberIds(raw.character_ids),
    locationId: typeof raw.location_id === "number" ? raw.location_id : null,
    propAssignments: propAssignments(raw.props),
    videoRefs,
    audioRefs,
    characters: shotCharacters(raw.characters),
    locationName: optionalString(raw.location_name),
    locationVariantName: optionalString(raw.location_variant_name),
    referenceImages: Array.isArray(raw.reference_images) ? (raw.reference_images as ReferenceImage[]) : [],
    cameraPresetIds: numberIds(raw.camera_preset_ids),
    videoText,
    videoDoc: (raw.video_kling_prompt_doc as JSONContent | null | undefined) ?? null,
    videoSystem,
  }
}

function mediaPatch(mapped: ShotMedia, prev: ShotMedia): ShotMedia {
  return {
    ...prev,
    shotKind: mapped.shotKind,
    videoDefault: mapped.videoDefault,
    videoSystemDefault: mapped.videoSystemDefault,
    frameFormat: mapped.frameFormat,
    videoModelKey: mapped.videoModelKey,
    videoResolution: mapped.videoResolution,
    videoDraft: mapped.videoDraft,
    hasVideoReferenceInput: mapped.hasVideoReferenceInput,
    storyboardPreviewUrl: mapped.storyboardPreviewUrl,
    videoClipUrl: mapped.videoClipUrl,
    providerTransient: mapped.providerTransient,
    characterIds: mapped.characterIds,
    locationId: mapped.locationId,
    propAssignments: mapped.propAssignments,
    videoRefs: mapped.videoRefs,
    audioRefs: mapped.audioRefs,
    characters: mapped.characters,
    locationName: mapped.locationName,
    locationVariantName: mapped.locationVariantName,
    referenceImages: mapped.referenceImages,
  }
}

function shotCatalogAsCanvas(model: GenerationModelCatalogEntry): CanvasModel {
  const ratios = (model.aspect_ratios ?? []).map((ratio) => ratio.trim()).filter(Boolean)
  return {
    id: model.id,
    provider: model.provider,
    label: model.label,
    media_kind: "video",
    aspect_ratios: ratios.length > 0 ? ratios : [...FRAME_FORMATS],
    durations: model.durations ?? [],
    // Shot audio is a project setting. Passing generate_audio would draw a toggle the shot cannot save.
    extra_params: (model.extra_params ?? [])
      .filter((param) => param.key !== "generate_audio")
      .map((param) => ({
        key: param.key,
        kind: param.kind,
        options: (param.options ?? []).map(String),
        default: param.default == null || param.default === "" ? "" : String(param.default),
      })),
    enabled: model.enabled !== false,
    platform_disabled: model.platform_disabled,
    restricted: model.restricted,
  }
}

function savedErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

export const FlowShotPrompt = forwardRef<FlowShotPromptHandle, FlowShotPromptProps>(function FlowShotPrompt(
  {
    projectId,
    frameFormat,
    frameRate,
    shotId,
    node,
    canEdit,
    onDisconnect,
    onMakeKeyframe,
    onAttach,
    onNodesChange,
    consumePendingAgent,
    chrome,
  },
  ref,
) {
  const { t } = useTranslation(["shot", "common", "workspace", "flow"])
  const page = usePage<FlowShotPageProps>()
  const project = page.props.project
  const characters = useMemo(() => page.props.project_cast ?? [], [page.props.project_cast])
  const locations = useMemo(() => page.props.project_locations ?? [], [page.props.project_locations])
  const propsList = useMemo(() => page.props.project_props ?? [], [page.props.project_props])
  const shotVideoModels = page.props.generation_models_by_context?.shot_video
  const jobs = useWorkspaceJobsStore((state) => state.jobs)
  const { requestSeedanceVideoGeneration, seedanceVideoConsentDialog } = useSeedanceVideoConsent(projectId)

  const editorRef = useRef<PromptEditorHandle>(null)
  const savedPromptRef = useRef({ text: "", system: "" })
  const savedPresetIdsRef = useRef<number[]>([])
  const overlayRef = useRef<FlowShotAgentPending | null>(null)
  const loadingRef = useRef(true)
  const mountedRef = useRef(true)
  const shotStateRef = useRef<ShotMedia | null>(null)
  const consumePendingRef = useRef(consumePendingAgent)
  const onNodesChangeRef = useRef(onNodesChange)
  const persistRef = useRef<() => Promise<boolean>>(async () => true)
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve())
  const presetsRef = useRef<CameraPickerPreset[]>([])
  const durationCoerceRef = useRef<string | null>(null)
  const seenLinksKey = useRef<string | null>(null)
  const seenShotId = useRef(shotId)
  consumePendingRef.current = consumePendingAgent
  onNodesChangeRef.current = onNodesChange

  const [shot, setShot] = useState<ShotMedia | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [videoText, setVideoText] = useState("")
  const [videoDoc, setVideoDoc] = useState<JSONContent | null>(null)
  const [videoSystem, setVideoSystem] = useState("")
  const [selectedPresetIds, setSelectedPresetIds] = useState<number[]>([])
  const [presets, setPresets] = useState<CameraPickerPreset[]>(() => getCachedAvailableCameraPresets(projectId) ?? [])
  const [cameraOpen, setCameraOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [promptRefreshKey, setPromptRefreshKey] = useState(0)
  const [generating, setGenerating] = useState(false)
  const [firstGenOpen, setFirstGenOpen] = useState(false)
  presetsRef.current = presets
  loadingRef.current = loading

  const linksKey = useMemo(() => {
    return (node.inputs ?? [])
      .filter((input) => (input.target_port ?? "references") === "links")
      .map((input) => `${input.id}:${input.source_node_id}`)
      .join(",")
  }, [node.inputs])

  // Текст извне (агент, история): в редактор, если он смонтирован, иначе только в состояние.
  const writeEditorText = useCallback((text: string): string => {
    const editor = editorRef.current
    if (!editor) {
      setVideoText(text)
      setVideoDoc(null)
      return text
    }
    editor.setExternalText(text)
    const snap = editor.getSnapshot()
    const nextText = snap?.text ?? text
    setVideoText(nextText)
    setVideoDoc(snap?.doc ?? null)
    return nextText
  }, [])

  const paintPrompt = useCallback(
    (text: string, cameraPresetIds?: number[]) => {
      const nextText = writeEditorText(text)
      savedPromptRef.current = { ...savedPromptRef.current, text: norm(nextText) }
      if (cameraPresetIds && cameraPresetIds.length > 0) {
        const ids = knownPresetIds(cameraPresetIds, presetsRef.current)
        if (ids.length > 0) {
          savedPresetIdsRef.current = ids
          setSelectedPresetIds(ids)
        }
      }
    },
    [writeEditorText],
  )

  const paintDuration = useCallback((durationTargetSec: number) => {
    setShot((prev) => (prev ? { ...prev, durationTargetSec, seedanceAdaptiveDuration: false } : prev))
  }, [])

  const applyLoaded = useCallback((mapped: ShotMedia, pending: FlowShotAgentPending | null) => {
    const nextText = pending?.text ?? mapped.videoText
    const nextDoc = pending?.text != null ? null : mapped.videoDoc
    const nextSystem = mapped.videoSystem
    const nextPresets =
      pending?.cameraPresetIds && pending.cameraPresetIds.length > 0
        ? knownPresetIds(pending.cameraPresetIds, presetsRef.current)
        : mapped.cameraPresetIds
    const presetIds = nextPresets.length > 0 || !pending?.cameraPresetIds ? nextPresets : mapped.cameraPresetIds
    setShot(
      pending?.durationTargetSec != null
        ? { ...mapped, durationTargetSec: pending.durationTargetSec, seedanceAdaptiveDuration: false }
        : mapped,
    )
    setVideoText(nextText)
    setVideoDoc(nextDoc)
    setVideoSystem(nextSystem)
    setSelectedPresetIds(presetIds)
    savedPromptRef.current = { text: norm(nextText), system: norm(nextSystem) }
    savedPresetIdsRef.current = presetIds
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setGenerating(false)
    setFirstGenOpen(false)
    durationCoerceRef.current = null
    loadingRef.current = true
    void shotPromptSaveTail
      .then(() => {
        if (cancelled) return null
        return fetchShotWorkspaceDetail(projectId, shotId)
      })
      .then((detail) => {
        if (cancelled || !detail) return
        const overlay = overlayRef.current
        overlayRef.current = null
        const parent = consumePendingRef.current(shotId)
        const pending = mergePending(parent, overlay)
        loadingRef.current = false
        applyLoaded(mapShotDetail(detail.shot as unknown as Record<string, unknown>), pending)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        loadingRef.current = false
        setError(err instanceof Error ? err.message : t("common.loadError"))
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [applyLoaded, projectId, promptRefreshKey, shotId, t])

  useEffect(() => {
    let cancelled = false
    setPresets(getCachedAvailableCameraPresets(projectId) ?? [])
    fetchAvailableCameraPresets(projectId)
      .then((rows) => {
        if (!cancelled) setPresets(rows)
      })
      .catch(() => {
        // Mention chips fall back to @cam[id] until presets load.
      })
    return () => {
      cancelled = true
    }
  }, [projectId])

  const refreshMedia = useCallback(() => {
    fetchShotWorkspaceDetail(projectId, shotId)
      .then(({ shot: raw }) => {
        const mapped = mapShotDetail(raw as unknown as Record<string, unknown>)
        if (mapped.id !== shotId) return
        setShot((prev) => (prev && prev.id === shotId ? mediaPatch(mapped, prev) : prev))
      })
      .catch(() => {
        // Keep the open prompt; the card reload still shows the new clip.
      })
  }, [projectId, shotId])

  const activeVideoJob = useMemo(() => activeShotJob(jobs, shotId, TAB_JOB_KINDS.video), [jobs, shotId])
  const onVideoJobFinished = useCallback(() => {
    refreshMedia()
  }, [refreshMedia])
  useReloadOnJobFinish(activeVideoJob, onVideoJobFinished)

  useEffect(() => {
    if (seenShotId.current !== shotId) {
      seenShotId.current = shotId
      seenLinksKey.current = linksKey
      return
    }
    if (seenLinksKey.current === null) {
      seenLinksKey.current = linksKey
      return
    }
    if (seenLinksKey.current === linksKey) return
    seenLinksKey.current = linksKey
    refreshMedia()
  }, [linksKey, refreshMedia, shotId])

  useImperativeHandle(
    ref,
    () => ({
      applyExternalPrompt: ({ text, cameraPresetIds }) => {
        if (loadingRef.current) {
          overlayRef.current = { ...(overlayRef.current ?? {}), text, cameraPresetIds }
          return
        }
        paintPrompt(text, cameraPresetIds)
      },
      applyDuration: (durationTargetSec) => {
        if (loadingRef.current) {
          overlayRef.current = { ...(overlayRef.current ?? {}), durationTargetSec }
          return
        }
        paintDuration(durationTargetSec)
      },
    }),
    [paintDuration, paintPrompt],
  )

  const persist = useCallback((): Promise<boolean> => {
    const run = async (): Promise<boolean> => {
      const current = shot
      if (!current || !canEdit) return true
      const snapshot = editorRef.current?.getSnapshot()
      const sentText = snapshot?.text ?? videoText
      const sentDoc = snapshot?.doc ?? videoDoc
      const sentSystem = videoSystem
      const saved = savedPromptRef.current
      if (norm(sentText) === saved.text && norm(sentSystem) === saved.system) return true

      try {
        const textValue = norm(sentText)
        const systemValue = norm(sentSystem)
        const custom = textValue !== "" && textValue !== current.videoDefault
        const body: PatchShotWorkspacePromptsBody = { skip_prompt_version: true }
        if (norm(sentText) !== saved.text) {
          body.video_kling_prompt = custom ? textValue : ""
          body.video_kling_prompt_doc = custom && sentDoc ? (sentDoc as Record<string, unknown>) : null
        }
        if (norm(sentSystem) !== saved.system) {
          body.video_system_prompt = systemValue === current.videoSystemDefault ? "" : systemValue
        }
        const promptSaved = Object.prototype.hasOwnProperty.call(body, "video_kling_prompt")
        await patchShotWorkspacePrompts(projectId, current.id, body)
        if (promptSaved) {
          try {
            const synced = await syncFlowShotLinks(projectId, node.id)
            onNodesChangeRef.current(synced.nodes)
          } catch (syncError: unknown) {
            toast.error(syncError instanceof Error ? syncError.message : t("common.loadSettingsError"))
          }
        }
        if (!mountedRef.current || shotStateRef.current?.id !== current.id) return true
        savedPromptRef.current = { text: norm(sentText), system: norm(sentSystem) }
        setError(null)
        return true
      } catch (err: unknown) {
        if (mountedRef.current && shotStateRef.current?.id === current.id) {
          setError(err instanceof Error ? err.message : t("common.loadSettingsError"))
        }
        return false
      }
    }

    const queued = enqueueSave(saveQueueRef, run)
    shotPromptSaveTail = shotPromptSaveTail.then(
      () => queued,
      () => queued,
    )
    return queued
  }, [canEdit, node.id, projectId, shot, t, videoDoc, videoSystem, videoText])
  persistRef.current = persist
  shotStateRef.current = shot

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      void persistRef.current()
    }
  }, [])

  useEffect(() => {
    if (!shot || !canEdit || loading) return
    if (norm(videoText) === savedPromptRef.current.text && norm(videoSystem) === savedPromptRef.current.system) return
    const timer = window.setTimeout(() => {
      void persist()
    }, 800)
    return () => window.clearTimeout(timer)
  }, [canEdit, loading, persist, shot, videoSystem, videoText])

  useEffect(() => {
    if (!shot || !canEdit || loading) return
    if (sameIds(selectedPresetIds, savedPresetIdsRef.current)) return
    const timer = window.setTimeout(() => {
      const next = [...selectedPresetIds]
      void persistRef.current().finally(() => {
        runWorkspace(
          projectId,
          "update_shot_presets",
          { shot_id: shot.id, camera_preset_ids: next },
          {
            onSuccess: () => {
              savedPresetIdsRef.current = next
            },
          },
        )
      })
    }, 300)
    return () => window.clearTimeout(timer)
  }, [canEdit, loading, projectId, selectedPresetIds, shot])

  const effectiveModelKey = effectiveShotVideoModelKey(shot?.videoModelKey, project)
  const shotVideoModel = findShotVideoModel(effectiveModelKey, shotVideoModels)
  const effectiveResolution = effectiveShotVideoResolution(shot?.videoResolution, project, shotVideoModel)
  const selectedCanvasModel = useMemo(() => {
    if (shotVideoModel) return shotCatalogAsCanvas(shotVideoModel)
    if (!effectiveModelKey && !project) return null
    return {
      id: effectiveModelKey,
      provider: project?.effective_video_generation_provider ?? "",
      label: effectiveModelKey,
      media_kind: "video" as const,
      aspect_ratios: [...FRAME_FORMATS],
      durations: [],
      extra_params: [],
      enabled: true,
    }
  }, [effectiveModelKey, project, shotVideoModel])
  const modelOptions = useMemo(() => {
    const options = (shotVideoModels ?? [])
      .filter((model) => model.enabled && !model.restricted)
      .map((model) => ({ value: model.id, label: model.label, disabled: false }))
    if (shotVideoModel && !options.some((option) => option.value === shotVideoModel.id)) {
      options.unshift({ value: shotVideoModel.id, label: shotVideoModel.label, disabled: true })
    }
    return options
  }, [shotVideoModel, shotVideoModels])
  const formatOptions = useMemo(
    () => aspectRatiosToFrameFormats(selectedCanvasModel?.aspect_ratios ?? []),
    [selectedCanvasModel],
  )
  const resolutionParams = useMemo((): Record<string, string> => {
    const params: Record<string, string> = {}
    if (effectiveResolution) params.resolution = effectiveResolution
    if (shot?.videoDraft === true) params.draft = "true"
    if (shot?.videoDraft === false) params.draft = "false"
    return params
  }, [effectiveResolution, shot?.videoDraft])
  const effectiveAspect = effectiveFrameFormat(shot?.frameFormat, project?.frame_format)
  const { durationOptions, currentDuration, adaptiveAllowed, adaptiveActive, videoPrice, supportsMultiShot } =
    useShotVideoGenerationSettings({
      projectId,
      project,
      shotVideoModel,
      shotVideoModels,
      effectiveModelKey,
      effectiveResolution,
      durationTargetSec: shot?.durationTargetSec ?? null,
      hasVideoReferenceInput: Boolean(shot?.hasVideoReferenceInput),
      seedanceAdaptiveDuration: Boolean(shot?.seedanceAdaptiveDuration),
    })

  useEffect(() => {
    if (!canEdit || !shot || durationOptions.length === 0) return
    const raw = shot.durationTargetSec
    const coerced = nearestDurationSec(raw, durationOptions)
    if (coerced == null || raw === coerced) return
    const key = `${shot.id}:${durationOptions.join(",")}`
    if (durationCoerceRef.current === key) return
    durationCoerceRef.current = key
    patchShotVideoSettings(projectId, shot.id, { duration_target_sec: coerced })
    setShot((prev) => (prev ? { ...prev, durationTargetSec: coerced } : prev))
  }, [canEdit, durationOptions, projectId, shot])

  const handleSelectDuration = useCallback(
    (dur: number) => {
      if (!shot) return
      const patch = nextShotDurationPatch({
        selected: dur,
        currentDurationSec: shot.durationTargetSec,
        adaptiveActive: Boolean(shot.seedanceAdaptiveDuration),
      })
      if (!patch) return
      void persistRef.current().finally(() => {
        patchShotVideoSettings(projectId, shot.id, patch)
        setShot((prev) => {
          if (!prev) return prev
          if (dur === ADAPTIVE_DURATION_SEC) return { ...prev, seedanceAdaptiveDuration: true }
          return { ...prev, durationTargetSec: dur, seedanceAdaptiveDuration: false }
        })
      })
    },
    [projectId, shot],
  )

  const handleToggleMultiShot = useCallback(
    (multi: boolean) => {
      if (!shot) return
      const next = multi ? "multi" : "single"
      patchShotVideoSettings(projectId, shot.id, { video_shot_type: next })
      setShot((prev) => (prev ? { ...prev, videoShotType: next } : prev))
    },
    [projectId, shot],
  )

  const handleModelChange = useCallback(
    (nextId: string) => {
      if (!shot || !project || !nextId) return
      const projectKey = project.effective_video_model?.toString().trim() || ""
      const nextModelOverride = nextId === projectKey ? null : nextId
      const prevModelOverride = shot.videoModelKey?.trim() || null
      const nextResolution = videoResolutionAfterModelChange(shot.videoResolution, nextId, shotVideoModels)
      const prevResolution = shot.videoResolution?.trim() || null
      const projectNorm = normalizeFrameFormat(project.frame_format)
      const currentAspect = effectiveFrameFormat(shot.frameFormat, project.frame_format)
      const nextCatalog = findShotVideoModel(nextId, shotVideoModels)
      const nextAspect = aspectRatioForModel(
        nextCatalog ? shotCatalogAsCanvas(nextCatalog) : null,
        currentAspect,
        project.frame_format,
      )
      const aspectSupported = (FRAME_FORMATS as readonly string[]).includes(nextAspect)
      const prevFrameOverride = isExplicitShotFrameOverride(shot.frameFormat)
        ? normalizeFrameFormat(shot.frameFormat)
        : null
      const nextFrameOverride =
        aspectSupported && nextAspect !== currentAspect
          ? nextAspect === projectNorm
            ? null
            : nextAspect
          : prevFrameOverride
      const modelChanged = nextModelOverride !== prevModelOverride
      const resolutionChanged = nextResolution !== prevResolution
      const frameChanged = nextFrameOverride !== prevFrameOverride
      const nextEffectiveResolution = effectiveShotVideoResolution(nextResolution, project, nextCatalog)
      const nextDraft = seedanceDraftEligible(nextId, nextEffectiveResolution) ? shot.videoDraft : null
      const draftChanged = nextDraft !== shot.videoDraft
      if (!modelChanged && !resolutionChanged && !frameChanged && !draftChanged) return

      const shotIdToSave = shot.id
      const prevDraft = shot.videoDraft
      const fail = t("common.saveErrorRetry")
      setShot((prev) =>
        prev && prev.id === shotIdToSave
          ? {
              ...prev,
              videoModelKey: nextModelOverride,
              videoResolution: resolutionChanged ? nextResolution : prev.videoResolution,
              frameFormat: nextFrameOverride,
              videoDraft: nextDraft,
            }
          : prev,
      )
      void (async () => {
        try {
          if (modelChanged) await patchShotVideoModelKey(projectId, shotIdToSave, nextModelOverride)
          else if (resolutionChanged) await patchShotVideoResolution(projectId, shotIdToSave, nextResolution)
        } catch (err: unknown) {
          if (!mountedRef.current) return
          setShot((prev) =>
            prev && prev.id === shotIdToSave
              ? {
                  ...prev,
                  videoModelKey: prevModelOverride,
                  videoResolution: prevResolution,
                  frameFormat: prevFrameOverride,
                  videoDraft: prevDraft,
                }
              : prev,
          )
          toast.error(savedErrorMessage(err, fail))
          return
        }
        if (!frameChanged) return
        try {
          await patchShotFrameFormat(projectId, shotIdToSave, nextFrameOverride)
        } catch (err: unknown) {
          if (!mountedRef.current) return
          setShot((prev) => (prev && prev.id === shotIdToSave ? { ...prev, frameFormat: prevFrameOverride } : prev))
          toast.error(savedErrorMessage(err, fail))
        }
      })()
    },
    [project, projectId, shot, shotVideoModels, t],
  )

  const handleAspectChange = useCallback(
    (format: FrameFormat) => {
      if (!shot || !project) return
      const projectNorm = normalizeFrameFormat(project.frame_format)
      const nextOverride = format === projectNorm ? null : format
      const prevOverride = isExplicitShotFrameOverride(shot.frameFormat) ? normalizeFrameFormat(shot.frameFormat) : null
      if (nextOverride === prevOverride) return
      const shotIdToSave = shot.id
      const fail = t("common.saveErrorRetry")
      setShot((prev) => (prev && prev.id === shotIdToSave ? { ...prev, frameFormat: nextOverride } : prev))
      void patchShotFrameFormat(projectId, shotIdToSave, nextOverride).catch((err: unknown) => {
        if (!mountedRef.current) return
        setShot((prev) => (prev && prev.id === shotIdToSave ? { ...prev, frameFormat: prevOverride } : prev))
        toast.error(savedErrorMessage(err, fail))
      })
    },
    [project, projectId, shot, t],
  )

  const handleResolutionChange = useCallback(
    (nextParams: Record<string, string>) => {
      if (!shot) return
      const next = nextParams.resolution?.trim() ?? ""
      const inherited = inheritedShotVideoResolution(project, shotVideoModel)
      const nextOverride = !next || next === inherited ? null : next
      const prevOverride = shot.videoResolution?.trim() || null
      const nextEffective = nextOverride ?? inherited
      const nextDraft = seedanceDraftEligible(effectiveModelKey, nextEffective)
        ? nextParams.draft === "true"
          ? true
          : nextParams.draft === "false"
            ? false
            : shot.videoDraft
        : null
      const draftChanged = nextDraft !== shot.videoDraft
      if (nextOverride === prevOverride && !draftChanged) return
      const shotIdToSave = shot.id
      const prevDraft = shot.videoDraft
      const fail = t("common.saveErrorRetry")
      setShot((prev) =>
        prev && prev.id === shotIdToSave ? { ...prev, videoResolution: nextOverride, videoDraft: nextDraft } : prev,
      )
      void (async () => {
        try {
          if (nextOverride !== prevOverride) await patchShotVideoResolution(projectId, shotIdToSave, nextOverride)
          if (draftChanged) await patchShotVideoDraft(projectId, shotIdToSave, nextDraft)
        } catch (err: unknown) {
          if (!mountedRef.current) return
          setShot((prev) =>
            prev && prev.id === shotIdToSave ? { ...prev, videoResolution: prevOverride, videoDraft: prevDraft } : prev,
          )
          toast.error(savedErrorMessage(err, fail))
        }
      })()
    },
    [effectiveModelKey, project, projectId, shot, shotVideoModel, t],
  )

  const videoBusy = generating || activeVideoJob != null
  const hasVideo = Boolean(shot?.videoClipUrl)
  const generateLabel = shot?.providerTransient
    ? t("canvas.modal.retryProviderTransient", { ns: "workspace" })
    : hasVideo
      ? t("common.regenerate")
      : t("common.generateVideo")

  const runGenerate = useCallback(
    async (videoResolutionOverride: string | null): Promise<boolean> => {
      if (!shot || videoBusy) return false
      if (!(await persistRef.current())) {
        toast.error(t("common.saveErrorRetry"))
        return false
      }
      let started = false
      await requestSeedanceVideoGeneration({
        shotIds: [shot.id],
        videoResolutionOverride,
        onStart: () => {
          started = true
          setGenerating(true)
        },
        visitOptions: {
          onFinish: () => {
            if (mountedRef.current) setGenerating(false)
          },
        },
      })
      return started
    },
    [requestSeedanceVideoGeneration, shot, t, videoBusy],
  )

  const { beforeArm: beforeArmGenerate, minOption: firstGenMinOption } = useFirstGenResolutionGate({
    needsReferenceFrame: Boolean(shot && shot.shotKind !== "transition" && !shot.storyboardPreviewUrl?.trim()),
    hasVideo,
    effectiveResolution,
    model: shotVideoModel,
    setDialogOpen: setFirstGenOpen,
  })

  const cameraMentionRows = useMemo(
    () => presets.map((preset) => ({ id: preset.id, name: preset.name, imageUrl: preset.demo_poster_url || null })),
    [presets],
  )
  const videoMentionItems = useMemo(() => {
    const references = [...(shot?.videoRefs ?? []), ...(shot?.audioRefs ?? [])].sort((a, b) => a.position - b.position)
    return buildMentionItems({
      characters,
      locations,
      props: propsList,
      references,
      cameras: cameraMentionRows,
      linkedCharacterIds: shot?.characterIds,
      linkedLocationIds: shot?.locationId != null ? [shot.locationId] : [],
      linkedPropAssignments: shot?.propAssignments,
    })
  }, [cameraMentionRows, characters, locations, propsList, shot])

  const linkedEntities = useMemo(() => {
    if (!shot) return null
    const images = shot.referenceImages
    return {
      characters: shot.characters.map((ch) => shotRefCharacter(images, ch.name, ch.outfitName)),
      props: shotRefProps(images),
      location: shotRefLocation(images, shot.locationName, shot.locationVariantName),
    }
  }, [shot])

  const cameraLabel = selectedPresetIds[0]
    ? (presets.find((preset) => preset.id === selectedPresetIds[0])?.name ?? t("promptEditor.segmentCamera"))
    : t("promptEditor.segmentCamera")

  const fill = Boolean(chrome?.fill)
  const editorReady = shot != null && !loading
  const cameraSelect = (
    <ShotCameraSelect
      projectId={projectId}
      label={cameraLabel}
      presets={presets}
      selectedIds={selectedPresetIds}
      onSelectedIdsChange={setSelectedPresetIds}
      open={cameraOpen}
      onOpenChange={setCameraOpen}
      onPresetsLoaded={setPresets}
    />
  )

  const settingsDisabled = !canEdit || videoBusy

  return (
    <>
      <div className={cn("flex min-h-0 flex-col gap-4", fill ? "h-full overflow-hidden pb-2" : "px-4 pt-3 pb-4")}>
        <FlowShotReferences
          className="it2tv-scrollbar max-h-48 flex-none"
          projectId={projectId}
          frameFormat={frameFormat}
          frameRate={frameRate}
          node={node}
          canEdit={canEdit}
          onDisconnect={onDisconnect}
          onMakeKeyframe={onMakeKeyframe}
          onAttach={onAttach}
        />
        {linkedEntities ? (
          <ShotReferencesPanel
            className="shrink-0"
            projectId={projectId}
            characters={linkedEntities.characters}
            props={linkedEntities.props}
            location={linkedEntities.location}
          />
        ) : null}
        {loading && !fill ? <p className="text-sm text-ui-text-secondary">{t("common.loadingPrompts")}</p> : null}
        {/* Колонка: пока шот грузится, рамка поля уже на месте — меняется только содержимое. */}
        {fill && !editorReady && !error ? (
          <PromptFieldLoadingFrame
            label={t("common.videoPrompt")}
            message={loading ? t("common.loadingPrompts") : null}
          />
        ) : null}
        {error ? (
          <p className="text-sm text-ui-status-failed" role="alert">
            {error}
          </p>
        ) : null}
        {shot && editorReady ? (
          <div className={cn(fill && "flex min-h-[140px] min-w-0 flex-1 flex-col")}>
            <div className="flex shrink-0 items-center justify-between gap-2">
              <label className="text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
                {t("common.videoPrompt")}
              </label>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-7 gap-1.5 px-2 text-[11px]"
                title={t("common.promptHistory")}
                onClick={() => setHistoryOpen(true)}
              >
                <History className="h-3.5 w-3.5" />
              </Button>
            </div>
            <FlowPromptComposer
              projectId={projectId}
              className={cn("mt-1.5", fill && "flex min-h-0 flex-1 flex-col")}
              footerHost={chrome?.footerHost}
              footerBar={(params) => (
                <GenerationBar
                  layout="stack"
                  paramsWrap
                  params={params}
                  label={generateLabel}
                  cost={videoPrice}
                  busy={generating && !activeVideoJob}
                  disabled={node.batch_waiting === true}
                  disabledReason={node.batch_waiting ? t("flow:bulk.queued") : undefined}
                  onBeforeArm={shot.providerTransient ? undefined : beforeArmGenerate}
                  onClick={() => runGenerate(null)}
                  confirmDelayKey={confirmDelayKey("shot", shotId, "video")}
                  activeJob={activeVideoJob}
                  projectId={projectId}
                />
              )}
              editor={
                <PromptEditor
                  ref={editorRef}
                  className={cn(fill && "min-h-0 flex-1", canEdit && "pb-14")}
                  valueText={videoText}
                  valueDoc={videoDoc}
                  seedKey={shotId}
                  mentionItems={videoMentionItems}
                  cameraPresets={presets}
                  cameraProjectId={projectId}
                  onCameraPresetsLoaded={setPresets}
                  onChange={(text, doc) => {
                    setVideoText(text)
                    setVideoDoc(doc)
                  }}
                  toolbarExtra={canEdit ? cameraSelect : undefined}
                  placeholder={t("placeholders.videoPrompt")}
                  ariaLabel={t("common.videoPrompt")}
                  showMentionHint={false}
                  editable={canEdit}
                  fillAvailable={fill}
                />
              }
              canEdit={canEdit}
              dictationDisabled={videoBusy}
              onDictation={(dictated) => editorRef.current?.insertTextAtCursor(dictated)}
              generate={
                <GenerateWithModelLabel
                  label={generateLabel}
                  busyLabel={generateLabel}
                  cost={videoPrice}
                  busy={videoBusy}
                  disabled={node.batch_waiting === true || videoBusy}
                  disabledReason={node.batch_waiting ? t("flow:bulk.queued") : undefined}
                  onBeforeArm={shot.providerTransient ? undefined : beforeArmGenerate}
                  onClick={() => runGenerate(null)}
                  confirmDelayKey={confirmDelayKey("shot", shotId, "video")}
                  buttonClassName="shadow-lg"
                />
              }
              attributesClassName="mt-2 shrink-0"
              attributes={
                <GenerationAttributeBar
                  mediaKind="video"
                  modelId={effectiveModelKey}
                  modelOptions={modelOptions}
                  onSelectModel={handleModelChange}
                  selectedModel={selectedCanvasModel}
                  disabled={settingsDisabled}
                  aspectRatio={effectiveAspect}
                  formatOptions={formatOptions}
                  onAspectRatio={handleAspectChange}
                  durationSec={adaptiveActive ? ADAPTIVE_DURATION_SEC : currentDuration}
                  durationOptions={durationOptions}
                  adaptiveAllowed={adaptiveAllowed}
                  onDuration={handleSelectDuration}
                  modelParams={resolutionParams}
                  onModelParams={handleResolutionChange}
                  seedanceDraftDefault={Boolean(project?.byteplus_seedance_draft_default)}
                  multiShot={
                    supportsMultiShot
                      ? {
                          checked: (shot.videoShotType ?? "single") === "multi",
                          onChange: handleToggleMultiShot,
                        }
                      : undefined
                  }
                />
              }
            />
          </div>
        ) : null}
      </div>
      {historyOpen ? (
        <PromptVersionsDialog
          projectId={projectId}
          shotId={shotId}
          kind="video"
          open
          onOpenChange={(open) => {
            if (!open) setHistoryOpen(false)
          }}
          onDataChanged={() => setPromptRefreshKey((key) => key + 1)}
          onPick={(systemPrompt, userPrompt) => {
            if (!canEdit) return
            setVideoSystem(systemPrompt)
            writeEditorText(userPrompt)
          }}
        />
      ) : null}
      {seedanceVideoConsentDialog}
      <FirstGenResolutionDialog
        open={firstGenOpen}
        onOpenChange={setFirstGenOpen}
        effectiveResolution={effectiveResolution}
        minOption={firstGenMinOption}
        busy={videoBusy}
        onGenerate={(videoResolutionOverride) => void runGenerate(videoResolutionOverride)}
      />
    </>
  )
})

function mergePending(
  parent: FlowShotAgentPending | null,
  overlay: FlowShotAgentPending | null,
): FlowShotAgentPending | null {
  if (!parent && !overlay) return null
  return { ...(parent ?? {}), ...(overlay ?? {}) }
}
